import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  forwardRef,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';

/**
 * Generic interface for hierarchical dropdown items
 * Can be used for entities, categories, departments, or any tree-like structure
 */
export interface HierarchicalItem {
  id: number;
  name: string;
  selected?: boolean;
  expanded?: boolean;
  children?: HierarchicalItem[];
}

/**
 * Configuration interface for MultiSelectDropdown component
 */
export interface MultiSelectDropdownConfig {
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  panelClass?: string;
  appearance?: 'fill' | 'outline';
  showError?: boolean;
  errorMessage?: string;
  validationMessages?: {
    required?: string;
    noSelection?: string;
  };
}

@Component({
  selector: 'educare-multiselect-dropdown',
  templateUrl: './multiselect-dropdown.component.html',
  styleUrls: ['./multiselect-dropdown.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectDropdownComponent),
      multi: true
    }
  ]
})
export class MultiSelectDropdownComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() items: HierarchicalItem[] = [];
  @Input() config: MultiSelectDropdownConfig = {};

  @Output() selectionChange = new EventEmitter<number[]>();
  @Output() dropdownOpenedChange = new EventEmitter<boolean>();

  selectedItemIds: number[] = [];
  dropdownOpened = false;
  private destroy$ = new Subject<void>();

  // ControlValueAccessor properties
  private onChange: (value: number[]) => void = () => {};
  private onTouched: () => void = () => {};
  private pendingValue: number[] | null = null; // Store value if set before items load

  // Default configuration
  private defaultConfig: MultiSelectDropdownConfig = {
    label: '',
    placeholder: 'common.placeholder.selectItems',
    disabled: false,
    required: false,
    panelClass: 'my-custom-panel',
    appearance: 'outline',
    showError: false,
    errorMessage: 'common.validations.selectItems',
    validationMessages: {
      required: 'Selection is required',
      noSelection: 'Please select at least one item'
    }
  };

  // Computed property to get merged configuration
  get mergedConfig(): MultiSelectDropdownConfig {
    return { ...this.defaultConfig, ...this.config };
  }

  constructor() {}

  ngOnInit(): void {
    // Apply pending value if items are already available
    if (this.pendingValue && this.pendingValue.length > 0 && this.items.length > 0) {
      this.markItemsAsSelected(this.pendingValue);
      this.pendingValue = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When items input changes, apply pending selection if exists
    if (changes['items'] && this.items.length > 0) {
      if (this.pendingValue && this.pendingValue.length > 0) {
        this.markItemsAsSelected(this.pendingValue);
        this.pendingValue = null;
      } else if (this.selectedItemIds.length > 0) {
        // Re-apply current selection when items change
        this.markItemsAsSelected(this.selectedItemIds);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: number[]): void {
    if (value && Array.isArray(value)) {
      this.selectedItemIds = value;
      if (this.items.length > 0) {
        // Items already loaded, apply selection immediately
        this.markItemsAsSelected(value);
      } else {
        // Items not loaded yet, store for later
        this.pendingValue = value;
      }
    }
  }

  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.config = { ...this.config, disabled: isDisabled };
  }

  /**
   * Check if all children are selected
   */
  isAllChildrenSelected(item: HierarchicalItem): boolean {
    return item.children?.every((child) => child.selected) ?? false;
  }

  /**
   * Check if item is in indeterminate state
   */
  isIndeterminate(item: HierarchicalItem): boolean {
    const selected = item.children?.filter((child) => child.selected).length ?? 0;
    return selected > 0 && selected < (item.children?.length ?? 0);
  }

  /**
   * Toggle item selection (parent and all children)
   */
  toggleItemSelection(item: HierarchicalItem, checked: boolean): void {
    item.selected = checked;
    if (item.children?.length) {
      item.children.forEach((child) => (child.selected = checked));
    }
    this.syncSelectedIds();
  }

  /**
   * Update parent selection based on children
   */
  updateParentSelection(): void {
    this.items.forEach((item) => {
      if (item.children?.length) {
        item.selected = this.isAllChildrenSelected(item);
      }
    });
    this.syncSelectedIds();
  }

  /**
   * Sync selected IDs and emit changes
   */
  syncSelectedIds(): void {
    this.selectedItemIds = this.getSelectedItemIds();
    this.onChange(this.selectedItemIds);
    this.onTouched();
    this.selectionChange.emit(this.selectedItemIds);
  }

  /**
   * Get all selected item IDs (parents and children)
   */
  getSelectedItemIds(): number[] {
    const ids: number[] = [];
    this.items.forEach((item) => {
      if (item.selected) {
        ids.push(item.id);
      }
      (item.children || []).forEach((child) => {
        if (child.selected) {
          ids.push(child.id);
        }
      });
    });
    return ids;
  }

  /**
   * Mark items as selected based on provided IDs
   */
  // Modify markItemsAsSelected to handle empty arrays properly
  private markItemsAsSelected(selectedIds: number[]): void {
    if (!this.items.length) return;

    // Ensure we handle empty arrays properly
    const normalizedIds = selectedIds?.map((id) => +id) || [];
    this.items = this.items.map((item) => this.updateItemSelection(item, normalizedIds));

    // Ensure expanded state is correct
    this.items.forEach((item) => {
      if (item.children?.some((child) => child.selected)) {
        item.expanded = true;
      }
    });
  }

  /**
   * Helper method to update item selection
   */
  private updateItemSelection(item: HierarchicalItem, normalizedIds: number[]): HierarchicalItem {
    const updatedChildren = (item.children || []).map((child) => ({
      ...child,
      selected: normalizedIds.includes(+child.id)
    }));
    const isParentSelected = normalizedIds.includes(+item.id);
    return {
      ...item,
      selected: isParentSelected,
      expanded: isParentSelected || updatedChildren.some((c) => c.selected),
      children: updatedChildren
    };
  }

  /**
   * Handle dropdown open/close
   */
  onDropdownOpenedChange(opened: boolean): void {
    this.dropdownOpened = opened;
    this.dropdownOpenedChange.emit(opened);
    if (!opened) {
      this.onTouched();
      return;
    }
    // When dropdown opens, scroll to first selected item if any, otherwise scroll to top
    // Use double requestAnimationFrame to ensure DOM is fully painted before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.scrollToSelectedOrTop();
      });
    });
  }

  /**
   * Scroll to the first selected item or to top if nothing is selected
   */
  private scrollToSelectedOrTop(): void {
    const panelClass = this.mergedConfig.panelClass || 'my-custom-panel';
    const panel = document.querySelector(`.${panelClass}`) as HTMLElement;
    if (!panel) {
      return;
    }
    // Temporarily add a class to prevent visual flicker during scroll positioning
    panel.classList.add('ng-animating');
    // Check if there are any selected items
    const hasSelection = this.selectedItemIds && this.selectedItemIds.length > 0;
    if (!hasSelection) {
      // No items selected - scroll to top
      panel.scrollTop = 0;
      // Remove the animating class after scroll is set
      requestAnimationFrame(() => panel.classList.remove('ng-animating'));
      return;
    }
    // Try to find the first selected checkbox using multiple selectors
    // Angular Material uses different classes in different versions
    const selectedCheckbox = panel.querySelector(
      '.mat-mdc-checkbox-checked, .mat-checkbox-checked, mat-checkbox.mat-mdc-checkbox-checked'
    ) as HTMLElement;
    if (selectedCheckbox) {
      // Find the parent container (entity-group or sub-entity)
      const checkboxContainer = selectedCheckbox.closest('.entity-group, .sub-entity') as HTMLElement;
      if (checkboxContainer) {
        // Use getBoundingClientRect for accurate positioning
        const panelRect = panel.getBoundingClientRect();
        const containerRect = checkboxContainer.getBoundingClientRect();
        // Calculate relative position
        const relativeTop = containerRect.top - panelRect.top + panel.scrollTop;
        // Scroll to position with offset for better visibility
        panel.scrollTop = Math.max(0, relativeTop - 50);
      } else {
        // Fallback: use offsetTop if closest() doesn't work
        const relativeTop = selectedCheckbox.offsetTop;
        panel.scrollTop = Math.max(0, relativeTop - 50);
      }
    } else {
      // Selected items exist but checkbox not found in DOM yet - scroll to top as fallback
      panel.scrollTop = 0;
    }
    // Remove the animating class after scroll is complete
    requestAnimationFrame(() => panel.classList.remove('ng-animating'));
  }
}
